'use client';

import { RefObject, useCallback, useEffect, useId, useRef, useState } from 'react';
import useEscapeKeydown from '@/hooks/useEsacapeKeydown';
import { IconBell } from '@/assets/icons/IconBell';
import { Text } from '@/ui-components/atoms/Text/Text';
import { SnapshotAlertPopover } from './SnapshotAlertPopover';
import { SnapshotAuthState } from './SnapshotUser.types';
import { useSnapshotAlerts } from './useSnapshotAlerts';
import { announceNavFlyoutOpen, useCloseOnOtherNavFlyoutOpen } from './navFlyoutEvents';
import styles from './snapshotpopover.module.scss';

const LINKNAME_PREFIX = 'AccountSnapshot:Alert:';

interface AnonymousAlertSnapshotIconProps {
    authState?: SnapshotAuthState;
    firstName?: string | null;
    isRestoringAuth?: boolean;
    onOpen?: () => void;
}

export function AnonymousAlertSnapshotIcon({
    authState = 'anonymous',
    firstName = null,
    isRestoringAuth = false,
    onOpen,
}: AnonymousAlertSnapshotIconProps = {}): React.JSX.Element {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverId = useId();
    const mappedAlerts = useSnapshotAlerts(authState);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    useCloseOnOtherNavFlyoutOpen('alerts', handleClose);

    const handleToggle = useCallback(() => {
        if (!open) {
            announceNavFlyoutOpen('alerts');
            onOpen?.();
        }
        setOpen(!open);
    }, [open, onOpen]);

    const isAuthenticated = authState === 'authenticated';

    const handleIconClick = useCallback(() => {
        const browserWindow = window as BrowserWindow;
        browserWindow.pushToAdobe({
            event: 'customer-info',
            customerInfo: {
                authenticationStatus: isAuthenticated ? 'Authenticated users' : 'Unidentified users',
            },
        });
        if (isAuthenticated) {
            browserWindow.pushToAdobe({
                event: 'account-snapshot-alert-displayed',
                alertInfo: { alertTypes: mappedAlerts?.alertTypes ?? [] },
            });
        }
    }, [isAuthenticated, mappedAlerts]);

    const prevIsAuthenticatedRef = useRef(isAuthenticated);
    useEffect(() => {
        const wasAuthenticated = prevIsAuthenticatedRef.current;
        prevIsAuthenticatedRef.current = isAuthenticated;
        if (wasAuthenticated && !isAuthenticated) {
            (window as BrowserWindow).pushToAdobe?.({ alertInfo: null });
        }
    }, [isAuthenticated]);

    useEffect(() => {
        const button = buttonRef.current;
        if (!button) return;
        button.addEventListener('click', handleIconClick, true);
        return () => button.removeEventListener('click', handleIconClick, true);
    }, [handleIconClick]);

    useEscapeKeydown(handleClose);
    const badgeCount = Math.max(0, mappedAlerts?.badgeCount ?? 0);
    const badgeLabel = badgeCount > 99 ? '99+' : `${badgeCount}`;
    const heading = firstName ? `Alerts for ${firstName}` : undefined;
    const authenticatedEmptyState =
        isAuthenticated && !mappedAlerts?.featuredAlert && (mappedAlerts?.alerts.length ?? 0) === 0 ? (
            <Text tag="p" className={styles.snapshotCenteredCopy}>
                No alerts right now.
            </Text>
        ) : undefined;

    return (
        <div className={styles.snapshotTrigger} data-componenttype="GlobalNavAlert">
            <button
                ref={buttonRef}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-controls={popoverId}
                aria-label={open ? 'Close alerts' : badgeCount > 0 ? `Open alerts, ${badgeLabel} notifications` : 'Open alerts'}
                className={styles.snapshotIconButton}
                data-linkname={
                    open
                        ? badgeCount > 0 ? `${LINKNAME_PREFIX}Close:${badgeCount}Notifications` : `${LINKNAME_PREFIX}Close`
                        : badgeCount > 0 ? `${LINKNAME_PREFIX}Open:${badgeCount}Notifications` : `${LINKNAME_PREFIX}Open`
                }
                onClick={handleToggle}
                type="button"
            >
                <IconBell />
                {badgeCount > 0 && (
                    <span
                        className={styles.snapshotNotificationBadge}
                        aria-hidden="true"
                    >
                        {badgeLabel}
                    </span>
                )}
            </button>

            {!isRestoringAuth ? (
                <SnapshotAlertPopover
                    alerts={mappedAlerts?.alerts}
                    buttonRef={buttonRef as RefObject<HTMLButtonElement | null>}
                    emptyState={authenticatedEmptyState}
                    featuredAlert={mappedAlerts?.featuredAlert}
                    heading={heading}
                    id={popoverId}
                    onClose={handleClose}
                    open={open}
                />
            ) : null}
        </div>
    );
}
